// components/AirportBookingForm.jsx
import React, { useEffect, useState } from 'react';
import {
  bookingService,
  buildSelectedVehiclePayloadContext,
  buildWebsiteEnquiryPayload,
  getAirportRateCardTripType,
} from '../../services/bookingService';
import { useLocation } from 'react-router-dom';
import LocationAutocompleteInput from './LocationAutocompleteInput';

const AirportBookingForm = ({ selectedVehicle, onClose, onSuccess, onError }) => {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    subject: '',
    message: '',
    bookingType: 'AIRPORT',
    vehicle: selectedVehicle?.name ,
    vehicleId: selectedVehicle?.id || null,
    tripType: 'City to Airport',
    pickupPoint: '',
    dropoffPoint: '',
    pickupDate: '',
    pickupTime: '',
    // Airport specific fields
    flightNumber: '',
    airportTerminal: ''
  });
const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
   const vehicleFromState = location.state?.vehicle;
  const vehicle = selectedVehicle || vehicleFromState;
  console.log('Selected Vehicle in AirportBookingForm:', vehicle);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.mobile.trim()) newErrors.mobile = 'Mobile is required';
    else if (!/^\d{10}$/.test(formData.mobile)) newErrors.mobile = 'Invalid mobile number';
    
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    if (!formData.pickupPoint.trim()) newErrors.pickupPoint = 'Pickup point is required';
    if (!formData.pickupDate) newErrors.pickupDate = 'Pickup date is required';
    if (!formData.pickupTime) newErrors.pickupTime = 'Pickup time is required';
    
    return newErrors;
  };

  const getAirportBackendTripType = () =>
    formData.tripType === 'Airport to City' ? 'Airport PickUp' : 'Airport Drop';


  const getSelectedRateCardTripType = () => getAirportRateCardTripType(formData.tripType);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleLocationChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  useEffect(() => {
    if (vehicle) {
      setFormData(prev => ({
        ...prev,
        vehicle: vehicle.name || '',
        vehicleId: vehicle.id || null,
        vehicleDetails: vehicle
      }));
    }
  }, [vehicle]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    
    try {
      const vehiclePayload = buildSelectedVehiclePayloadContext({
        vehicle,
        bookingType: 'Airport Taxi',
        rateCardTripType: getSelectedRateCardTripType(),
      });
      const payload = buildWebsiteEnquiryPayload({
        customerName: formData.name,
        customerEmail: formData.email,
        customerPhone: formData.mobile,
        subject: formData.subject,
        message: formData.message,
        expectedTravelDate: formData.pickupDate,
        expectedPax: vehiclePayload.expectedPax,
        expectedBudget: vehiclePayload.expectedBudget || String(vehicle?.price || '').replace(/[^0-9.]/g, '') || undefined,
        selectedVehicleIds: vehiclePayload.selectedVehicleIds,
        selectedVehicleTypes: vehiclePayload.selectedVehicleTypes.length
          ? vehiclePayload.selectedVehicleTypes
          : [vehicle?.type || formData.vehicle || 'AIRPORT'],
        pickupLocation: formData.pickupPoint,
        dropLocation: formData.dropoffPoint,
        tripType: getAirportBackendTripType(),
        preferredVehicleCategory: 'STANDARD',
        customFields: {
          bookingType: 'AIRPORT',
          pickupTime: formData.pickupTime,
          tripVariant: formData.tripType,
          rateCardTripType: getSelectedRateCardTripType(),
          flightNumber: formData.flightNumber || undefined,
          airportTerminal: formData.airportTerminal || undefined,
          vehiclePreference: formData.vehicle || undefined,
          vehicleDetails: vehiclePayload.vehicleDetails,
          rateCard: vehiclePayload.rateCard,
        },
      });

      const response = await bookingService.submitWebsiteEnquiry(payload);
      
      if (onSuccess) {
        onSuccess(response);
      } else {
        alert('Booking submitted successfully!');
      }
      
      // Reset form or close modal
      if (onClose) onClose();
      
    } catch (error) {
      console.error('Booking failed:', error);
      if (onError) {
        onError(error.message);
      } else {
        alert('Booking failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      background: "#fff",
      borderRadius: 24,
      padding: 24,
      boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
      marginBottom: 20,
      fontFamily: "'Nunito', sans-serif"
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 20
    },
    title: {
      margin: 0,
      fontSize: 20,
      fontWeight: 800,
      color: "#1A1A1A"
    },
    closeBtn: {
      background: "none",
      border: "none",
      fontSize: 20,
      cursor: "pointer",
      width: 32,
      height: 32,
      borderRadius: 16,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#666",
      transition: "all 0.2s"
    },
    form: {
      display: "grid",
      gap: 16
    },
    grid3: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: 12
    },
    grid2: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 12
    },
    input: {
      padding: 12,
      borderRadius: 8,
      border: "1.5px solid #E0E0E0",
      fontSize: 14,
      width: "100%",
      outline: "none",
      transition: "border-color 0.2s"
    },
    inputError: {
      border: "1.5px solid #ff4444"
    },
    label: {
      display: "block",
      fontSize: 12,
      fontWeight: 700,
      color: "#666",
      marginBottom: 4
    },
    radioGroup: {
      display: "flex",
      gap: 20,
      padding: "8px 0"
    },
    radioLabel: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 14,
      cursor: "pointer"
    },
    errorText: {
      color: "#ff4444",
      fontSize: 11,
      marginTop: 4,
      fontWeight: 500
    },
    submitBtn: {
      background: "#FFD600",
      border: "none",
      padding: "16px",
      borderRadius: 12,
      fontSize: 16,
      fontWeight: 800,
      color: "#1A1A1A",
      cursor: "pointer",
      marginTop: 8,
      transition: "all 0.2s",
      boxShadow: "0 4px 14px rgba(255,214,0,0.4)",
      opacity: loading ? 0.7 : 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>
          Airport Transfer {selectedVehicle && `- ${selectedVehicle.name}`}
        </h3>
        {onClose && (
          <button 
            onClick={onClose} 
            style={styles.closeBtn}
            onMouseEnter={(e) => e.target.style.background = "#f0f0f0"}
            onMouseLeave={(e) => e.target.style.background = "none"}
          >
            ✕
          </button>
        )}
      </div>
      
      <form onSubmit={handleSubmit} style={styles.form}>
        {/* Personal Details */}
        <div style={styles.grid3}>
          <div>
            <input
              type="text"
              name="name"
              placeholder="Name *"
              value={formData.name}
              onChange={handleChange}
              style={{...styles.input, ...(errors.name ? styles.inputError : {})}}
            />
            {errors.name && <div style={styles.errorText}>{errors.name}</div>}
          </div>
          <div>
            <input
              type="tel"
              name="mobile"
              placeholder="Mobile *"
              value={formData.mobile}
              onChange={handleChange}
              style={{...styles.input, ...(errors.mobile ? styles.inputError : {})}}
            />
            {errors.mobile && <div style={styles.errorText}>{errors.mobile}</div>}
          </div>
          <div>
            <input
              type="email"
              name="email"
              placeholder="Email *"
              value={formData.email}
              onChange={handleChange}
              style={{...styles.input, ...(errors.email ? styles.inputError : {})}}
            />
            {errors.email && <div style={styles.errorText}>{errors.email}</div>}
          </div>
        </div>

        <div>
          <label style={styles.label}>Subject *</label>
          <input
            type="text"
            name="subject"
            placeholder="Enter subject"
            value={formData.subject}
            onChange={handleChange}
            style={{...styles.input, ...(errors.subject ? styles.inputError : {})}}
          />
          {errors.subject && <div style={styles.errorText}>{errors.subject}</div>}
        </div>

        <div>
          <label style={styles.label}>Message *</label>
          <textarea
            name="message"
            placeholder="Enter message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            style={{...styles.input, resize: 'vertical', ...(errors.message ? styles.inputError : {})}}
          />
          {errors.message && <div style={styles.errorText}>{errors.message}</div>}
        </div>

        {/* Booking Type & Vehicle */}
        <div style={styles.grid2}>
          <div>
            <label style={styles.label}>Booking Type</label>
            <input
              type="text"
              name="bookingType"
              value="AIRPORT TAXI"
              readOnly
              style={{...styles.input, background: "#F5F5F5"}}
            />
          </div>
          <div>
            <label style={styles.label}>Vehicle</label>
            <input
              type="text"
              name="vehicle"
              value={formData.vehicle}
              readOnly
              style={{...styles.input, background: "#F5F5F5"}}
            />
          </div>
        </div>

        {/* Trip Type Radio */}
        <div>
          <label style={styles.label}>Trip Type</label>
          <div style={styles.radioGroup}>
            <label style={styles.radioLabel}>
              <input
                type="radio"
                name="tripType"
                value="City to Airport"
                checked={formData.tripType === 'City to Airport'}
                onChange={handleChange}
              />
              City to Airport
            </label>
            <label style={styles.radioLabel}>
              <input
                type="radio"
                name="tripType"
                value="Airport to City"
                checked={formData.tripType === 'Airport to City'}
                onChange={handleChange}
              />
              Airport to City
            </label>
          </div>
        </div>

        {/* Flight Details (Optional) */}
        <div style={styles.grid2}>
          <div>
            <label style={styles.label}>Flight Number (Optional)</label>
            <input
              type="text"
              name="flightNumber"
              placeholder="e.g., AI101"
              value={formData.flightNumber}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
          <div>
            <label style={styles.label}>Terminal (Optional)</label>
            <input
              type="text"
              name="airportTerminal"
              placeholder="e.g., T1, T2"
              value={formData.airportTerminal}
              onChange={handleChange}
              style={styles.input}
            />
          </div>
        </div>

        {/* Pickup & Dropoff */}
        <div style={styles.grid2}>
          <div>
            <label style={styles.label}>Pickup Point *</label>
            <LocationAutocompleteInput
              name="pickupPoint"
              placeholder="Enter pickup location"
              value={formData.pickupPoint}
              onChange={(value) => handleLocationChange('pickupPoint', value)}
              inputStyle={{...styles.input, ...(errors.pickupPoint ? styles.inputError : {})}}
            />
            {errors.pickupPoint && <div style={styles.errorText}>{errors.pickupPoint}</div>}
          </div>
          <div>
            <label style={styles.label}>Dropoff Point</label>
            <LocationAutocompleteInput
              name="dropoffPoint"
              value={formData.dropoffPoint}
              placeholder="Enter dropoff location"
              onChange={(value) => handleLocationChange('dropoffPoint', value)}
              inputStyle={{...styles.input, background: "#F5F5F5"}}
            />
          </div>
        </div>

        {/* Date & Time */}
        <div style={styles.grid2}>
          <div>
            <label style={styles.label}>Pickup Date *</label>
            <input
              type="date"
              name="pickupDate"
              value={formData.pickupDate}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
              style={{...styles.input, ...(errors.pickupDate ? styles.inputError : {})}}
            />
            {errors.pickupDate && <div style={styles.errorText}>{errors.pickupDate}</div>}
          </div>
          <div>
            <label style={styles.label}>Pickup Time *</label>
            <input
              type="time"
              name="pickupTime"
              value={formData.pickupTime}
              onChange={handleChange}
              style={{...styles.input, ...(errors.pickupTime ? styles.inputError : {})}}
            />
            {errors.pickupTime && <div style={styles.errorText}>{errors.pickupTime}</div>}
          </div>
        </div>

        {/* Book Now Button */}
        <button
          type="submit"
          disabled={loading}
          style={styles.submitBtn}
          onMouseEnter={(e) => !loading && (e.target.style.transform = "translateY(-2px)")}
          onMouseLeave={(e) => !loading && (e.target.style.transform = "translateY(0)")}
        >
          {loading ? 'PROCESSING...' : 'BOOK NOW'}
          {!loading && ' →'}
        </button>
      </form>
    </div>
  );
};

export default AirportBookingForm;
